import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, X, Upload, Camera } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useImageUpload from "@/hooks/useImageUpload";

const INTEREST_OPTIONS = [
  "Music", "Movies", "Gaming", "Travel", "Fitness", "Art", "Photography",
  "Cooking", "Reading", "Dancing", "Hiking", "Yoga", "Tech", "Fashion",
  "Sports", "Anime", "Coffee", "Wine", "Dogs", "Cats", "Nature", "Beach",
];

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    bio: "",
    age: "",
    gender: "",
    interested_in: "",
    city: "",
    interests: [],
    picture: "",
    photos: [],
  });

  const { uploadImage, uploading } = useImageUpload();
  const [customInterest, setCustomInterest] = useState("");

  const addCustomInterest = () => {
    const trimmed = customInterest.trim();
    if (!trimmed) return;
    if (trimmed.length > 30) {
      toast.error("Keep it short (30 chars max)");
      return;
    }
    if (formData.interests.length >= 10) {
      toast.error("Maximum 10 interests allowed");
      return;
    }
    if (formData.interests.some((i) => i.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("You already have that one!");
      return;
    }
    setFormData({ ...formData, interests: [...formData.interests, trimmed] });
    setCustomInterest("");
  };

  const removeInterest = (interest) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter((i) => i !== interest),
    });
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        age: user.age?.toString() || "",
        gender: user.gender || "",
        interested_in: user.interested_in || "",
        city: user.location?.city || "",
        interests: user.interests || [],
        picture: user.picture || "",
        photos: user.photos || [],
      });
    }
  }, [user]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await uploadImage(file);
    if (url) {
      setFormData({ ...formData, picture: url });
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (formData.photos.length >= 6) {
        toast.error("Maximum 6 photos allowed");
        break;
      }
      const url = await uploadImage(file);
      if (url) {
        setFormData((prev) => ({ ...prev, photos: [...prev.photos, url] }));
      }
    }
  };

  const removePhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        bio: formData.bio,
        age: formData.age ? parseInt(formData.age) : null,
        gender: formData.gender || null,
        interested_in: formData.interested_in || null,
        location: formData.city ? { city: formData.city } : null,
        interests: formData.interests,
        picture: formData.picture || null,
        photos: formData.photos,
      };

      const response = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        await refreshUser();
        toast.success("Profile updated!");
        navigate(`/profile/${user.user_id}`);
      } else {
        throw new Error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const generateBio = async () => {
    if (formData.interests.length === 0) {
      toast.error("Add some interests first!");
      return;
    }

    setGeneratingBio(true);
    try {
      const response = await fetch(`${API}/ai/generate-bio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          interests: formData.interests,
          personality_traits: [],
          tone: "friendly",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, bio: data.suggestions });
        toast.success("Bio generated! Edit it to make it yours.");
      } else {
        throw new Error("Failed to generate bio");
      }
    } catch (error) {
      toast.error("Failed to generate bio");
    } finally {
      setGeneratingBio(false);
    }
  };

  const toggleInterest = (interest) => {
    if (formData.interests.includes(interest)) {
      setFormData({
        ...formData,
        interests: formData.interests.filter((i) => i !== interest),
      });
    } else if (formData.interests.length < 10) {
      setFormData({
        ...formData,
        interests: [...formData.interests, interest],
      });
    } else {
      toast.error("Maximum 10 interests allowed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5E1]">
      {/* Header */}
      <header className="app-header">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="brutal-btn bg-white p-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold">Edit Profile</h1>
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Picture */}
          <div className="brutal-card">
            <Label className="font-bold text-lg mb-4 block">Profile Photo</Label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-24 h-24 border-2 border-black bg-gray-200 overflow-hidden flex-shrink-0">
                  {formData.picture ? (
                    <img
                      src={formData.picture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-black">
                      {formData.name?.[0] || "?"}
                    </div>
                  )}
                </div>
                <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#FF4D4D] text-white border-2 border-black flex items-center justify-center cursor-pointer">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#4A4A4A] mb-2">Upload a photo or paste URL</p>
                <Input
                  type="url"
                  placeholder="Image URL"
                  value={formData.picture}
                  onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
                  className="brutal-input"
                  data-testid="picture-input"
                />
              </div>
            </div>
          </div>

          {/* Photo Gallery */}
          <div className="brutal-card">
            <Label className="font-bold text-lg mb-4 block">Photos ({formData.photos.length}/6)</Label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {formData.photos.map((photo, index) => (
                <div key={photo} className="relative aspect-square border-2 border-black overflow-hidden">
                  <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-1 right-1 w-6 h-6 bg-[#FF4D4D] text-white border-2 border-black flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {formData.photos.length < 6 && (
                <label className="aspect-square border-2 border-dashed border-black flex items-center justify-center cursor-pointer hover:bg-[#E3F4EA] transition-colors">
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-1 text-[#4A4A4A]" />
                    <span className="text-xs text-[#4A4A4A]">Add Photo</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            {uploading && (
              <p className="text-sm text-[#4A4A4A]">Uploading...</p>
            )}
          </div>

          {/* Basic Info */}
          <div className="brutal-card">
            <h2 className="font-bold text-lg mb-4">Basic Info</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="font-bold">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="brutal-input mt-1"
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="age" className="font-bold">Age</Label>
                  <Input
                    id="age"
                    type="number"
                    min="18"
                    max="100"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="brutal-input mt-1"
                    data-testid="age-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="font-bold">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="brutal-input mt-1"
                    placeholder="Your city"
                    data-testid="city-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold">Gender</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(v) => setFormData({ ...formData, gender: v })}
                  >
                    <SelectTrigger className="brutal-input mt-1" data-testid="gender-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-black bg-white z-[100]">
                      <SelectItem value="male" data-testid="gender-male">Male</SelectItem>
                      <SelectItem value="female" data-testid="gender-female">Female</SelectItem>
                      <SelectItem value="non-binary" data-testid="gender-nonbinary">Non-binary</SelectItem>
                      <SelectItem value="other" data-testid="gender-other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-bold">Interested In</Label>
                  <Select
                    value={formData.interested_in}
                    onValueChange={(v) => setFormData({ ...formData, interested_in: v })}
                  >
                    <SelectTrigger className="brutal-input mt-1" data-testid="interested-in-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-black bg-white z-[100]">
                      <SelectItem value="male" data-testid="interested-male">Men</SelectItem>
                      <SelectItem value="female" data-testid="interested-female">Women</SelectItem>
                      <SelectItem value="everyone" data-testid="interested-everyone">Everyone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="brutal-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg">Bio</h2>
              <button
                type="button"
                onClick={generateBio}
                disabled={generatingBio}
                className="brutal-btn bg-[#E8E6F8] py-2 text-sm disabled:opacity-50"
                data-testid="generate-bio-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generatingBio ? "Generating..." : "AI Generate"}
              </button>
            </div>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell people about yourself..."
              className="brutal-input min-h-[120px]"
              maxLength={500}
              data-testid="bio-input"
            />
            <p className="text-sm text-[#4A4A4A] mt-2">{formData.bio.length}/500</p>
          </div>

          {/* Interests */}
          <div className="brutal-card">
            <h2 className="font-bold text-lg mb-4">
              Interests <span className="font-normal text-[#4A4A4A]">({formData.interests.length}/10)</span>
            </h2>

            {/* Custom add-your-own */}
            <div className="mb-3 flex gap-2">
              <input
                type="text"
                value={customInterest}
                onChange={(e) => setCustomInterest(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomInterest(); } }}
                placeholder="Add your own interest (e.g. roller derby)"
                maxLength={30}
                className="brutal-input flex-1"
                data-testid="custom-interest-input"
              />
              <button
                type="button"
                onClick={addCustomInterest}
                className="px-3 py-1 font-bold border-2 border-black bg-[#FFD700] hover:bg-[#FFC700]"
                data-testid="custom-interest-add-btn"
              >
                + Add
              </button>
            </div>

            {/* Your picks (custom + selected presets) */}
            {formData.interests.length > 0 && (
              <div className="mb-3 p-2 border-2 border-[#FF4D4D] bg-[#FFF1F1]">
                <p className="text-xs font-bold uppercase text-[#FF4D4D] mb-1">Your picks</p>
                <div className="flex flex-wrap gap-1">
                  {formData.interests.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => removeInterest(interest)}
                      className="text-xs px-2 py-0.5 bg-[#FF4D4D] text-white font-bold border border-black"
                      data-testid={`selected-interest-${interest.toLowerCase()}`}
                    >
                      {interest} <X className="w-3 h-3 ml-0.5 inline" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-[#4A4A4A] mb-2">Or pick from popular ones:</p>
            <div className="flex flex-wrap gap-2">
              {INTEREST_OPTIONS.map((interest) => {
                const isSelected = formData.interests.includes(interest);
                return (
                  <motion.button
                    key={interest}
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => toggleInterest(interest)}
                    className={`px-3 py-1 font-bold border-2 border-black transition-colors ${
                      isSelected
                        ? "bg-[#FF4D4D] text-white"
                        : "bg-white hover:bg-[#E3F4EA]"
                    }`}
                    data-testid={`interest-${interest.toLowerCase()}`}
                  >
                    {interest}
                    {isSelected && <X className="w-4 h-4 ml-1 inline" />}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Customize Profile Link */}
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/customize")}
            className="brutal-card w-full text-left bg-[#E8E6F8]"
            data-testid="customize-profile-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1A1A1A] border-2 border-black flex items-center justify-center text-white">
                {"</>"}
              </div>
              <div>
                <h3 className="font-bold text-lg">Customize Your Profile</h3>
                <p className="text-sm text-[#4A4A4A]">Add custom HTML/CSS to make it yours!</p>
              </div>
            </div>
          </motion.button>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="brutal-btn-primary w-full text-lg disabled:opacity-50"
            data-testid="save-profile-btn"
          >
            {loading ? "Saving..." : "Save Profile"}
          </button>
        </form>
      </main>
    </div>
  );
}
